<script setup lang="ts">
import { ZoomIn, Play, ArrowLeft, ArrowRight, X } from 'lucide-vue-next';

interface GalleryItem {
	id: string;
	directus_file?: string | { id: string; type?: string | null; title?: string | null } | null;
	video_preview_image?: string | { id: string } | null;
	sort?: number;
}

interface GalleryProps {
	data: {
		id: string;
		tagline?: string;
		headline?: string;
		text_alignment?: 'left' | 'center' | 'right' | null;
		items: GalleryItem[];
	};
	background?: 'light' | 'dark' | null;
}

const props = defineProps<GalleryProps>();

const isLightboxOpen = ref(false);
const currentIndex = ref(0);

const sortedItems = computed(() => {
	if (!props.data.items) return [];
	return [...props.data.items].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
});

const currentItem = computed(() => {
	if (!sortedItems.value.length || currentIndex.value < 0 || currentIndex.value >= sortedItems.value.length) {
		return null;
	}

	return sortedItems.value[currentIndex.value];
});

function handleOpenLightbox(index: number) {
	if (index >= 0 && index < sortedItems.value.length) {
		currentIndex.value = index;
		isLightboxOpen.value = true;
	}
}

function handlePrev() {
	if (!sortedItems.value.length) return;
	currentIndex.value = currentIndex.value > 0 ? currentIndex.value - 1 : sortedItems.value.length - 1;
}

function handleNext() {
	if (!sortedItems.value.length) return;
	currentIndex.value = currentIndex.value < sortedItems.value.length - 1 ? currentIndex.value + 1 : 0;
}

function handleKeyDown(e: KeyboardEvent) {
	if (!isLightboxOpen.value) return;
	e.preventDefault();
	e.stopPropagation();

	switch (e.key) {
		case 'ArrowLeft':
			handlePrev();
			break;
		case 'ArrowRight':
			handleNext();
			break;
		case 'Escape':
			isLightboxOpen.value = false;
			break;
	}
}

const { setAttr } = useVisualEditing();

// Helper function per ottenere l'ID del file
function getFileId(file: string | { id: string; type?: string | null; title?: string | null } | null | undefined): string | null {
	if (!file) {
		return null;
	}
	return typeof file === 'string' ? file : file.id;
}

// Helper function per ottenere il title del file
function getFileTitle(file: string | { id: string; type?: string | null; title?: string | null } | null | undefined): string | null {
	if (!file || typeof file === 'string') {
		return null;
	}
	return file.title || null;
}

// Helper function per ottenere l'ID dell'immagine di anteprima
function getPreviewImageId(previewImage: string | { id: string } | null | undefined): string | null {
	if (!previewImage) {
		return null;
	}
	return typeof previewImage === 'string' ? previewImage : previewImage.id;
}

// Helper function per determinare se un file è un video
function isVideo(file: string | { id: string; type?: string | null } | null | undefined): boolean {
	if (!file) {
		return false;
	}
	if (typeof file === 'string') {
		return false; // Se è solo una stringa, assumiamo che sia un'immagine
	}
	if (!file.type) {
		return false;
	}
	return file.type.startsWith('video/');
}

const textColor = computed(() => {
	// Se il background è dark, il testo è bianco
	if (props.background === 'dark') {
		return 'text-white';
	}
	// Se il background è light o null, il testo è nero
	return 'text-black';
});

const textAlignmentClasses = computed(() => {
	if (!props.data.text_alignment) {
		// Se text_alignment non è definito, usa il comportamento di default (left)
		return {
			'text-left items-start': true,
		};
	}
	
	const alignment = props.data.text_alignment;
	return {
		'text-left items-start': alignment === 'left',
		'text-center items-center': alignment === 'center',
		'text-right items-end': alignment === 'right',
	};
});

onMounted(() => {
	window.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
	window.removeEventListener('keydown', handleKeyDown);
});
</script>

<template>
	<section class="relative">
		<div
			v-if="data.tagline || data.headline"
			class="flex flex-col gap-4"
			:class="textAlignmentClasses"
		>
			<Tagline
				v-if="data.tagline"
				:tagline="data.tagline"
				:data-directus="setAttr({ collection: 'block_gallery', item: data.id, fields: 'tagline', mode: 'popover' })"
			/>
			<Headline
				v-if="data.headline"
				:headline="data.headline"
				:text-color="textColor"
				:data-directus="setAttr({ collection: 'block_gallery', item: data.id, fields: 'headline', mode: 'popover' })"
			/>
		</div>

		<div
			v-if="sortedItems.length"
			class="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
			:data-directus="setAttr({ collection: 'block_gallery', item: data.id, fields: 'items', mode: 'modal' })"
		>
			<template
				v-for="(item, index) in sortedItems"
				:key="item.id"
			>
				<div
					v-if="item.directus_file"
					class="relative overflow-hidden group hover:shadow-lg transition-shadow duration-300"
					:class="isVideo(item.directus_file) ? 'aspect-[2/3] cursor-pointer' : 'aspect-[2/3] cursor-pointer rounded-lg'"
					@click="handleOpenLightbox(index)"
				>
					<!-- Video preview con immagine, title e pulsante play -->
					<template v-if="isVideo(item.directus_file)">
						<div class="relative w-full h-full">
							<!-- Immagine di anteprima come sfondo -->
							<DirectusImage
								v-if="getPreviewImageId(item.video_preview_image)"
								:uuid="getPreviewImageId(item.video_preview_image)!"
								:alt="getFileTitle(item.directus_file) || 'Video preview'"
								fill
								sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
								class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
							/>
							<!-- Fallback se non c'è immagine di anteprima -->
							<div
								v-else
								class="w-full h-full bg-gray-900"
							/>
							
							<!-- Pulsante play e title centrati -->
							<div class="absolute inset-0 flex flex-col items-center justify-center bg-black/20 hover:bg-black/30 transition-colors duration-300">
								<button
									@click.stop="handleOpenLightbox(index)"
									class="group/play mb-3"
									aria-label="Play video"
								>
									<div class="bg-white/90 rounded-full p-2 group-hover/play:bg-white group-hover/play:scale-110 transition-all duration-300">
										<Play class="w-4 h-4 text-accent" fill="currentColor" />
									</div>
								</button>
								
								<!-- Title sotto il pulsante play -->
								<p
									v-if="getFileTitle(item.directus_file)"
									class="text-2xl font-medium text-white text-center px-4"
								>
									{{ getFileTitle(item.directus_file) }}
								</p>
							</div>
						</div>
					</template>
					
					<!-- Immagine normale -->
					<template v-else>
						<DirectusImage
							:uuid="getFileId(item.directus_file)!"
							:alt="`Gallery item ${item.id}`"
							fill
							sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
							class="w-full h-full object-cover rounded-lg"
						/>

						<div
							class="absolute inset-0 bg-white bg-opacity-60 opacity-0 group-hover:opacity-100 flex justify-center items-center transition-opacity duration-300"
						>
							<ZoomIn class="w-10 h-10 text-gray-800" />
						</div>
					</template>
				</div>
			</template>
		</div>

		<Dialog v-model:open="isLightboxOpen">
			<DialogContent
				class="flex max-w-full max-h-full items-center justify-center p-2 bg-transparent border-none z-50"
				hideCloseButton
			>
				<DialogTitle class="sr-only">Gallery Media</DialogTitle>
				<DialogDescription class="sr-only">
					Viewing media {{ currentIndex + 1 }} of {{ sortedItems.length }}.
				</DialogDescription>
				<DialogHeader />

				<div class="relative w-[90vw] h-[90vh] flex items-center justify-center">
					<div
						v-if="currentItem && currentItem.directus_file && isVideo(currentItem.directus_file)"
						class="flex items-center justify-center"
						style="aspect-ratio: 2/3; width: min(calc(90vh * 2 / 3), 90vw); height: min(90vh, calc(90vw * 3 / 2));"
					>
						<DirectusVideo
							:uuid="getFileId(currentItem.directus_file)!"
							:title="getFileTitle(currentItem.directus_file)"
							:show-title="false"
							:autoplay="true"
							class="w-full h-full object-contain"
						/>
					</div>
					<DirectusImage
						v-else-if="currentItem && currentItem.directus_file"
						:uuid="getFileId(currentItem.directus_file)!"
						:alt="`Gallery item ${currentItem.id}`"
						class="size-full object-contain"
					/>
				</div>

				<div v-if="sortedItems.length > 1" class="absolute bottom-4 inset-x-0 flex justify-between px-4">
					<button
						class="flex items-center gap-2 text-white bg-black bg-opacity-70 rounded-full px-4 py-2 hover:bg-opacity-90"
						@click="handlePrev"
					>
						<ArrowLeft class="w-8 h-8" />
						<span>Prev</span>
					</button>
					<button
						class="flex items-center gap-2 text-white bg-black bg-opacity-70 rounded-full px-4 py-2 hover:bg-opacity-90"
						@click="handleNext"
					>
						<span>Next</span>
						<ArrowRight class="w-8 h-8" />
					</button>
				</div>
				<DialogClose asChild>
					<button
						class="absolute top-4 right-4 bg-black bg-opacity-70 text-white rounded-full p-2 hover:bg-opacity-90"
						aria-label="Close Lightbox"
					>
						<X class="w-8 h-8" />
					</button>
				</DialogClose>
			</DialogContent>
		</Dialog>
	</section>
</template>
